import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z, zodResolver } from "@pos/shared";
import type { StoreOption } from "@pos/shared/types";
import {
  Badge,
  Button,
  Field,
  FieldLabel,
  FieldError,
  FieldGroup,
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  useForm,
  Controller,
} from "@pos/ui";

interface StoreSelectFormProps {
  stores: StoreOption[];
  onSubmit: (selectedStore: string, password: string) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  deviceConflictError?: string | null;
  onForceLogin?: (() => void) | null;
}

const storeSelectSchema = z.object({
  selectedStore: z.string().min(1, "Please select a store"),
  password: z.string().min(1, "Password is required"),
});

type StoreSelectFormValues = z.infer<typeof storeSelectSchema>;

export function StoreSelectForm({
  stores,
  onSubmit,
  onBack,
  isLoading,
  error,
  deviceConflictError,
  onForceLogin,
}: StoreSelectFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<StoreSelectFormValues>({
    resolver: zodResolver(storeSelectSchema),
    defaultValues: {
      selectedStore: stores.length === 1 ? stores[0].shopId : "",
      password: "",
    },
  });

  function handleSubmit(values: StoreSelectFormValues) {
    onSubmit(values.selectedStore, values.password);
  }

  return (
    <div className="w-full">
      <div className="mb-6 space-y-1">
        <h2 className="text-3xl font-bold tracking-tight">Select Store</h2>
        <p className="text-base text-muted-foreground">
          Choose a store and re-enter your password to continue
        </p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FieldGroup>
          <Controller
            name="selectedStore"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Store</FieldLabel>
                <Combobox
                  items={stores}
                  value={stores.find((s) => s.shopId === field.value) ?? null}
                  onValueChange={(val) => field.onChange(val?.shopId ?? "")}
                  itemToStringLabel={(item) => {
                    const extra = item as unknown as Record<string, unknown>;
                    return [item.storeName, extra.groupname, extra.customerid]
                      .filter(Boolean)
                      .join(" · ");
                  }}
                  itemToStringValue={(item) => item.shopId}
                >
                  <ComboboxInput
                    placeholder="Search stores..."
                    disabled={isLoading}
                  />
                  <ComboboxContent>
                    <ComboboxList>
                      {(item) => {
                        const extra = item as unknown as Record<
                          string,
                          unknown
                        >;
                        return (
                          <ComboboxItem key={item.shopId} value={item}>
                            <span className="flex flex-col gap-1 py-0.5">
                              <span>{item.storeName}</span>
                              <span className="flex gap-1.5">
                                {typeof extra.groupname === "string" && (
                                  <Badge variant="secondary" className="w-fit">
                                    {extra.groupname}
                                  </Badge>
                                )}
                                {typeof extra.customerid === "string" && (
                                  <Badge variant="outline" className="w-fit">
                                    {extra.customerid}
                                  </Badge>
                                )}
                              </span>
                            </span>
                          </ComboboxItem>
                        );
                      }}
                    </ComboboxList>
                    <ComboboxEmpty>No stores found</ComboboxEmpty>
                  </ComboboxContent>
                </Combobox>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Password</FieldLabel>
                <InputGroup>
                  <InputGroupInput
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    aria-invalid={fieldState.invalid}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-xs"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="size-4 text-muted-foreground" />
                      ) : (
                        <Eye className="size-4 text-muted-foreground" />
                      )}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive">
              {error}
            </div>
          )}

          {deviceConflictError && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive">
              <p>{deviceConflictError}</p>
              {onForceLogin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onForceLogin}
                  disabled={isLoading}
                >
                  Force Login
                </Button>
              )}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In →"}
          </Button>

          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to login
          </button>
        </FieldGroup>
      </form>
    </div>
  );
}
